/* eslint-disable react-hooks/exhaustive-deps */
import {
  getUpdatedStepData,
  convertToTz,
  formatMillisToDateTime,
} from './utils';
import { NotificationStepsProps } from '../NotificationForm';
import { notificationPublishStepValidator } from '../notification-validators';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  VStack,
  FormHelperText,
  Grid,
  GridItem,
  HStack,
} from '@chakra-ui/react';
import { tz } from 'moment-timezone';
import { Controller, useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { Select, RadioGroup } from '@ssar-app/uikit';
import { ReactNode, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers';
import dayjs, { extend as dayJsExtend } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  NotificationType,
  notificationTimeZonesList,
  UnitOfTimeValues,
  NotificationDetails,
} from '@ssar-app/domain';
import relativePlugin from 'dayjs/plugin/relativeTime';

dayJsExtend(utc);
dayJsExtend(relativePlugin);
dayJsExtend(timezone);

export const PublishNotification = (props: NotificationStepsProps) => {
  const {
    notification,
    setNotification,
    stepOptions,
    setStepOptions,
    triggerStepValidation,
  } = props;

  const [dateTimeLocal, setDateTimelocal] = useState(
    isEmpty(notification?.details?.startTime) ? true : false,
  );
  const [stepData, setStepData] = useState<NotificationDetails>({
    ...notification.details,
  });
  const { getValues, errors, control, trigger, register, setValue } =
    useForm<NotificationDetails>({
      defaultValues: {
        ...notification.details,
      },
      resolver: yupResolver(notificationPublishStepValidator, {
        abortEarly: false,
      }),
      mode: 'all',
      reValidateMode: 'onChange',
      criteriaMode: 'all',
    });

  useEffect(() => {
    register('startTime', { required: true });
    register('endTime', { required: true });
    register('duration');
    register('unitOfTime');
    register('timeZone');
  }, []);

  useEffect(() => {
    setNotification({
      ...notification,
      details: { ...stepData },
    });
  }, [stepData]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setStepOptions(getUpdatedStepData(stepOptions, 2, true));
    } else {
      setStepOptions(getUpdatedStepData(stepOptions, 2, false));
    }
  }, [Object.keys(errors).length]);

  triggerStepValidation.current = () =>
    trigger(Object.keys(getValues()) as (keyof NotificationDetails)[]);

  const getTimezoneCurrentTime = () => {
    const toSet: ReactNode[] = [];
    let sSecs = -1;
    notificationTimeZonesList.forEach((lTz) => {
      if (sSecs === -1) {
        sSecs = Number(dayjs().tz(lTz).format('ss'));
      }
      const dTz = tz(lTz).format('z');
      const dDate = dayjs().tz(lTz).format('DD MMM YYYY');
      const dTime = dayjs().tz(lTz).format('hh:mm A');
      toSet.push(
        <>
          {dDate}
          <br />
          {dTime} {dTz}
        </>,
      );
    });
    setTimezoneTimes(toSet);
    setTimeout(() => {
      getTimezoneCurrentTime();
    }, (60 - sSecs) * 1000);
  };

  const [timezoneTimes, setTimezoneTimes] = useState<ReactNode[]>([]);

  useEffect(() => {
    getTimezoneCurrentTime();
  }, []);

  useEffect(() => {
    stepData.startTime && trigger(['startTime']);
    stepData.endTime && trigger(['endTime']);
  }, [stepData.timeZone, getValues().startTime, getValues().endTime]);

  useEffect(() => {
    if (notification.details.endTime) {
      trigger(['endTime']);
    }
  }, [notification.details.startTime]);

  return (
    <Flex direction='column' minW='sm' mr='6'>
      <VStack alignItems='start' mb='7'>
        <Text fontSize='lg' fontWeight='semibold'>
          Step 2: Publish Settings
        </Text>
        <Text fontSize='sm'>
          {notification.type === NotificationType.ANNOUNCEMENT
            ? 'You can set the timezone and display duration of the announcement here.'
            : 'You can set the maintenance downtime and set the hours/days to alert the users before the maintenance date here.'}
        </Text>
      </VStack>
      <Flex direction={'column'}>
        <>
          {notification.type === NotificationType.MAINTENANCE ? (
            <Text fontSize='sm' mb={4} fontWeight={'semibold'}>
              Maintenance Duration
            </Text>
          ) : (
            <Text fontSize='sm' mb={4} fontWeight={'semibold'}>
              Notification Display Duration
            </Text>
          )}
          <Text fontSize='sm' mb={2}>
            Select Your Time Zone
          </Text>
          <RadioGroup
            optionsType='button'
            variant='solid'
            onChange={(val) => {
              setValue('timeZone', val);
              setStepData({
                ...stepData,
                timeZone: val,
              });
            }}
            value={stepData.timeZone}
            options={timezoneTimes.map((lTz, index) => {
              return {
                label: lTz,
                value: notificationTimeZonesList[index],
              };
            })}
            radioButtonProps={{
              shadow: 'none',
              borderRadius: 'none',
            }}
            applyBorderRadiusForStartEndButtons={true}
            borderRadiusForStartEndButtons={5}
          />
        </>
        <Grid templateColumns='repeat(24,1fr)' mt='6'>
          <GridItem colSpan={11}>
            <FormControl isRequired isInvalid={!!errors.startTime} mb={6}>
              <FormLabel fontSize='sm' htmlFor='startTime'>
                {notification.type === NotificationType.MAINTENANCE
                  ? 'Start Date & Time'
                  : 'Display After'}
              </FormLabel>
              <Controller
                name='startTime'
                control={control}
                defaultValue={stepData.startTime}
                render={({ value, onChange }) => {
                  return (
                    <>
                      {!dateTimeLocal && stepData.startTime && (
                        <Input
                          type='text'
                          value={formatMillisToDateTime(
                            stepData.startTime,
                            stepData.timeZone,
                          )}
                          onClick={() => setDateTimelocal(true)}
                          sx={{ borderColor: 'gray.300' }}
                        />
                      )}
                      {dateTimeLocal && (
                        <Input
                          type='datetime-local'
                          onChange={(e) => {
                            const sTimeMills = convertToTz(
                              e.target.value,
                              stepData.timeZone,
                            ).valueOf();
                            onChange(sTimeMills);
                            setStepData({
                              ...stepData,
                              startTime: sTimeMills,
                            });
                            trigger(['startTime']);
                          }}
                          onKeyDown={(e) => {
                            e.preventDefault();
                          }}
                          value={
                            value
                              ? formatMillisToDateTime(value, stepData.timeZone)
                              : ''
                          }
                          sx={{ borderColor: 'gray.300' }}
                        />
                      )}
                    </>
                  );
                }}
              />
              <FormErrorMessage>
                {errors.startTime?.message?.replaceAll(
                  'startTime',
                  'Start Date & Time',
                )}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={2} />
          <GridItem colSpan={11}>
            <FormControl isRequired isInvalid={!!errors.endTime} mb={6}>
              <FormLabel fontSize='sm' htmlFor='endTime'>
                {notification.type === NotificationType.MAINTENANCE
                  ? 'End Date & Time'
                  : 'Display Until'}
              </FormLabel>
              <Controller
                name='endTime'
                control={control}
                defaultValue={stepData.endTime}
                render={({ value, onChange }) => {
                  return (
                    <>
                      {!dateTimeLocal && stepData.endTime && (
                        <Input
                          type='text'
                          value={formatMillisToDateTime(
                            stepData.endTime,
                            stepData.timeZone,
                          )}
                          onClick={() => setDateTimelocal(true)}
                          sx={{ borderColor: 'gray.300' }}
                        />
                      )}
                      {dateTimeLocal && (
                        <Input
                          type='datetime-local'
                          onChange={(e) => {
                            const sTimeMills = convertToTz(
                              e.target.value,
                              stepData.timeZone,
                            ).valueOf();
                            onChange(sTimeMills);
                            setStepData({
                              ...stepData,
                              endTime: sTimeMills,
                            });
                            trigger(['endTime']);
                          }}
                          onKeyDown={(e) => {
                            e.preventDefault();
                          }}
                          value={
                            value
                              ? formatMillisToDateTime(value, stepData.timeZone)
                              : ''
                          }
                          sx={{ borderColor: 'gray.300' }}
                        />
                      )}
                    </>
                  );
                }}
              />
              <FormErrorMessage>
                {errors.endTime?.message?.replaceAll(
                  'endTime',
                  'End Date & Time',
                )}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>
      </Flex>
      {notification.type === NotificationType.MAINTENANCE && (
        <Flex direction={'column'} gap={3}>
          <Text fontSize='sm' fontWeight={'semibold'}>
            Publish Time
          </Text>
          <Text fontSize='sm'>
            The number of days or hours entered in the box will be used to
            determine how many days or hours before the starting date the
            notification will be published.
          </Text>
          <Flex direction={'row'}>
            <FormControl mb={6} isInvalid={!!errors.duration}>
              <FormLabel fontSize='sm' htmlFor='duration'>
                Number of days/hours
              </FormLabel>
              <HStack>
                <Controller
                  name='duration'
                  control={control}
                  defaultValue={stepData.duration}
                  render={({ value, onChange }) => (
                    <Input
                      type='number'
                      min={0}
                      onChange={(e) => {
                        onChange(e.target.value);
                        setValue('duration', Number(e.target.value));
                        trigger(['duration']);
                        setStepData({
                          ...stepData,
                          duration: Number(e.target.value),
                        });
                      }}
                      value={value}
                      sx={{
                        borderRadius: 0,
                        borderColor: 'gray.500',
                        maxW: '28',
                      }}
                    />
                  )}
                />
                <Controller
                  name='unitOfTime'
                  control={control}
                  defaultValue={stepData.unitOfTime.toString()}
                  render={({ value, onChange }) => (
                    <Select
                      selectContainerProps={{
                        w: 28,
                        borderRadius: 0,
                        height: '40px',
                        left: -2,
                      }}
                      searchBoxProps={{ color: 'black' }}
                      placeholder='Select Type'
                      onChange={(value) => {
                        onChange(value);
                        setValue('unitOfTime', Number(value));
                        trigger(['unitOfTime']).then(() =>
                          trigger(['duration']),
                        );
                        setStepData({
                          ...stepData,
                          unitOfTime: Number(value),
                        });
                      }}
                      options={UnitOfTimeValues}
                      optionLabelProps={{ color: 'black' }}
                      noOptionsText='No Notification Type Found'
                      defaultValue={value}
                    />
                  )}
                />
              </HStack>
              <FormErrorMessage>
                <FormErrorMessage>{errors.duration?.message}</FormErrorMessage>
              </FormErrorMessage>
              <FormHelperText>Use 0 to publish now</FormHelperText>
            </FormControl>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
